// Mission-Impossible style intel popouts + voice readout.
//
// When people or animals are locked on, a HUD popout flies in near a locked
// subject showing one intel item (a gaze-science factor, a fringe wow claim, or
// a deep-research finding) with corner brackets, a scanline, and a typewritten
// headline, and the Speaker reads the headline aloud (Web Speech API, on-device).
//
// Speech needs a user gesture to start, so main.ts calls speaker.enable() from
// the start-gate tap. Only one utterance plays at a time; overlapping requests
// are dropped so the voice never stampedes.

import { nextIntel, type IntelItem } from "../vision/intel";
import { popoutDocHtml } from "./intelDoc";
import { tearOffToWindow } from "./multiscreen";
import type { SpeakOptions } from "../draw/types";

/** Wraps window.speechSynthesis with a gesture-gated, one-at-a-time policy. */
export class Speaker {
  private enabled = false;
  private queue: string[] = [];
  private draining = false;
  private synth: SpeechSynthesis | null =
    typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;

  /** Call inside a user gesture (the start tap) to unlock audio. */
  enable(): void {
    if (!this.synth) return;
    this.enabled = true;
    // Warm the engine with a near-silent utterance inside the gesture so the
    // first real line speaks without the browser's first-use delay.
    try {
      const warm = new SpeechSynthesisUtterance(" ");
      warm.volume = 0;
      this.synth.speak(warm);
    } catch {
      // ignore
    }
  }

  /** Toggle voice on/off; returns the new state. Cancels any current speech. */
  toggle(): boolean {
    if (!this.synth) return false;
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.synth.cancel();
      this.queue = [];
      this.draining = false;
    }
    return this.enabled;
  }

  get on(): boolean {
    return this.enabled;
  }

  /** Speak a line, unless muted or something is already playing. */
  speak(text: string): void {
    if (!this.enabled || !this.synth) return;
    if (this.synth.speaking || this.synth.pending) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.06;
      u.pitch = 0.85;
      u.volume = 1;
      this.synth.speak(u);
    } catch {
      // A speech failure must never break the render loop.
    }
  }

  /**
   * Ordered speech. Unlike speak() (which DROPS overlapping utterances so the
   * live HUD never stampedes), this QUEUES lines and plays them one after
   * another via utterance.onend. Draw Mode uses it for the ASMR ritual and the
   * spoken psychographic report, where every line must be heard in order.
   */
  speakQueued(text: string, opts: SpeakOptions = {}): void {
    if (!this.enabled || !this.synth) return;
    this.queue.push(text);
    if (!this.draining) this.drain(opts);
  }

  private drain(opts: SpeakOptions): void {
    if (!this.synth) return;
    const next = this.queue.shift();
    if (next === undefined) {
      this.draining = false;
      return;
    }
    this.draining = true;
    try {
      const u = new SpeechSynthesisUtterance(next);
      u.rate = opts.rate ?? 0.92;
      u.pitch =
        opts.pitch ??
        (opts.tone === "analytical" ? 0.8 : opts.tone === "empathetic" ? 1.0 : 0.85);
      u.volume = opts.volume ?? 1;
      u.onend = () => this.drain(opts);
      u.onerror = () => this.drain(opts);
      this.synth.speak(u);
    } catch {
      this.draining = false;
    }
  }

  /** Clear any queued lines and stop current speech immediately. */
  stopQueue(): void {
    this.queue = [];
    this.draining = false;
    this.synth?.cancel();
  }
}

interface LivePopout {
  el: HTMLElement;
  expireAt: number;
}

/** Normalized anchor (0..1) in image space, roughly where a locked subject is. */
export interface Anchor {
  x: number;
  y: number;
}

const SPAWN_INTERVAL_MS = 3800;
const AMBIENT_INTERVAL_MS = 4600;
const POPOUT_TTL_MS = 5200;
const MAX_ACTIVE = 3;
// Approximate popout footprint, used to keep freshly-spawned panels on-screen.
const POPOUT_W = 300;
const POPOUT_H = 200;

// Screen positions used for ambient popouts when nobody is locked on, so the
// HUD stays populated with the fringe / anomaly intel stream regardless.
const AMBIENT_POS: Anchor[] = [
  { x: 0.12, y: 0.3 },
  { x: 0.7, y: 0.24 },
  { x: 0.18, y: 0.58 },
  { x: 0.66, y: 0.6 },
  { x: 0.4, y: 0.42 },
];

export class PopoutDeck {
  private live: LivePopout[] = [];
  private lastSpawn = 0;
  private anchorIdx = 0;

  constructor(private container: HTMLElement, private speaker: Speaker) {}

  /**
   * Present a specific intel item on demand (used by Draw Mode to fly in the
   * psychographic report sections). Unlike pulse(), it does not speak: the
   * caller drives ordered speech via Speaker.speakQueued so sections are not
   * spoken twice.
   */
  present(item: IntelItem, anchor: Anchor = { x: 0.5, y: 0.3 }, ttlMs?: number): void {
    const now = performance.now();
    this.spawn(item, anchor, now);
    if (ttlMs != null) {
      const last = this.live[this.live.length - 1];
      if (last) last.expireAt = now + ttlMs;
    }
  }

  /**
   * Call every frame with the anchors of currently locked subjects. Throttles
   * to one popout every SPAWN_INTERVAL_MS while at least one subject is locked,
   * expires old popouts, and reads each new one aloud.
   */
  pulse(anchors: Anchor[], now: number): void {
    // Expire finished popouts.
    for (const p of this.live) {
      if (now >= p.expireAt && !p.el.classList.contains("po-out")) {
        p.el.classList.add("po-out");
        const el = p.el;
        window.setTimeout(() => el.remove(), 360);
      }
    }
    this.live = this.live.filter((p) => now < p.expireAt + 400);

    if (this.live.length >= MAX_ACTIVE) return;

    // When someone is locked on, anchor popouts to them and fire faster; when
    // nobody is locked, keep the HUD alive with ambient popouts on a slower beat.
    const haveAnchor = anchors.length > 0;
    const interval = haveAnchor ? SPAWN_INTERVAL_MS : AMBIENT_INTERVAL_MS;
    if (now - this.lastSpawn < interval) return;

    const item = nextIntel();
    if (!item) return;

    const pool = haveAnchor ? anchors : AMBIENT_POS;
    const anchor = pool[this.anchorIdx % pool.length]!;
    this.anchorIdx++;
    this.spawn(item, anchor, now);
    this.speaker.speak(item.title);
    this.lastSpawn = now;
  }

  private spawn(item: IntelItem, anchor: Anchor, now: number): void {
    const el = document.createElement("div");
    el.className = `po po-${item.kind} po-in`;

    // Place near the subject but keep it on-screen. Anchor is normalized image
    // space; treating it as viewport space is approximate (cover crop) but fine
    // for a HUD popout that just needs to sit beside the subject. We position in
    // pixels (not %) so a dragged panel keeps its exact spot.
    const rect = this.container.getBoundingClientRect();
    const px = Math.min(rect.width - POPOUT_W - 8, Math.max(8, anchor.x * rect.width));
    const py = Math.min(rect.height - POPOUT_H - 8, Math.max(8, anchor.y * rect.height));
    el.style.left = `${Math.round(px)}px`;
    el.style.top = `${Math.round(py)}px`;

    // The body is now a REAL <iframe> rendering the shared HUD document, so each
    // popout is a first-class surface that can be torn off into its own OS window
    // and dragged onto another monitor (or clean off the screen).
    el.innerHTML = `
      <span class="po-b po-tl"></span><span class="po-b po-tr"></span>
      <span class="po-b po-bl"></span><span class="po-b po-br"></span>
      <div class="po-chrome">
        <span class="po-name">▸ ${escapeHtml(item.tag)}</span>
        <button class="po-btn po-pop" type="button" title="tear off into its own window">⤢</button>
        <button class="po-btn po-x" type="button" title="dismiss">✕</button>
      </div>`;
    const frame = document.createElement("iframe");
    frame.className = "po-frame";
    frame.setAttribute("sandbox", "allow-scripts");
    frame.setAttribute("scrolling", "no");
    frame.setAttribute("title", item.title);
    frame.srcdoc = popoutDocHtml(item);
    el.appendChild(frame);

    this.wireInteraction(el, item);
    this.container.appendChild(el);
    // Drop the enter class next frame so the CSS transition runs.
    requestAnimationFrame(() => el.classList.remove("po-in"));

    this.live.push({ el, expireAt: now + POPOUT_TTL_MS });
  }

  /** Make a popout draggable, tear it off when flung past the edge or on ⤢. */
  private wireInteraction(el: HTMLElement, item: IntelItem): void {
    const tear = (): void => {
      const r = el.getBoundingClientRect();
      const win = tearOffToWindow(item, { w: Math.max(320, r.width), h: Math.max(200, r.height) });
      if (win) {
        el.classList.add("po-torn");
        window.setTimeout(() => el.remove(), 220);
      } else {
        // Pop-ups blocked: keep the panel but tell the user why nothing detached.
        el.classList.add("po-blocked");
        const name = el.querySelector<HTMLElement>(".po-name");
        if (name) name.textContent = "▸ allow pop-ups to tear off";
      }
    };

    el.querySelector<HTMLButtonElement>(".po-pop")?.addEventListener("click", (e) => {
      e.stopPropagation();
      tear();
    });
    el.querySelector<HTMLButtonElement>(".po-x")?.addEventListener("click", (e) => {
      e.stopPropagation();
      el.classList.add("po-out");
      window.setTimeout(() => el.remove(), 300);
    });

    // Pointer drag. Freezing expiry (data-pinned) once grabbed so a panel the
    // user is interacting with does not vanish mid-drag.
    let dragging = false;
    let sx = 0, sy = 0, ox = 0, oy = 0;
    const down = (e: PointerEvent): void => {
      if ((e.target as HTMLElement).closest(".po-btn")) return;
      dragging = true;
      el.dataset.pinned = "1";
      el.classList.add("po-drag");
      const r = el.getBoundingClientRect();
      const c = this.container.getBoundingClientRect();
      ox = r.left - c.left;
      oy = r.top - c.top;
      sx = e.clientX;
      sy = e.clientY;
      el.setPointerCapture(e.pointerId);
      e.preventDefault();
    };
    const move = (e: PointerEvent): void => {
      if (!dragging) return;
      el.style.left = `${ox + (e.clientX - sx)}px`;
      el.style.top = `${oy + (e.clientY - sy)}px`;
    };
    const up = (): void => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove("po-drag");
      // Flung to (or past) any screen edge → detach into a real, free window.
      const r = el.getBoundingClientRect();
      const off =
        r.right < 56 || r.left > window.innerWidth - 56 ||
        r.bottom < 56 || r.top > window.innerHeight - 56;
      if (off) tear();
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] ?? c);
}
